/**
 * Modal dialog for setting a Sign action on a player.
 * Shows Transfer Fee, Annual Salary (MoneyInputs) and Duration (months).
 * @module features/squad-planning/components/SignActionModal
 */

import React, { memo } from "react";

import LiveImpactPanel from "@/features/financial-compliance/components/molecules/LiveImpactPanel";
import MoneyInput from "@/components/molecules/MoneyInput";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Heading } from "@/components/atoms/Heading";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";

import type { LiveImpactModel } from "@/features/financial-compliance/types";

/** Props for {@link SignActionModalView} */
export interface SignActionModalViewProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Player name for the title */
  readonly playerName: string;
  /** Current transfer fee value */
  readonly transferFee: number | null;
  /** Current annual salary value */
  readonly annualSalary: number | null;
  /** Current duration text (months) */
  readonly durationText: string;
  /** Current signing bonus value */
  readonly signingBonus: number | null;
  /** Current agent fee value */
  readonly agentFee: number | null;
  /** Called when transfer fee changes */
  readonly onTransferFeeChange: (value: number | null) => void;
  /** Called when annual salary changes */
  readonly onAnnualSalaryChange: (value: number | null) => void;
  /** Called when duration text changes */
  readonly onDurationChange: (text: string) => void;
  /** Called when signing bonus changes */
  readonly onSigningBonusChange: (value: number | null) => void;
  /** Called when agent fee changes */
  readonly onAgentFeeChange: (value: number | null) => void;
  /** Called to confirm the sign action */
  readonly onConfirm: () => void;
  /** Called to cancel/close the modal */
  readonly onCancel: () => void;
  /** Called to remove the action (edit mode only) */
  readonly onCancelAction?: () => void;
  /** Whether the modal is in edit mode */
  readonly isEditMode?: boolean;
  /** Live Impact verdict for the scenario after this action (SE-7002) */
  readonly liveImpact: LiveImpactModel;
  /** Opens the full impact view for the current scenario */
  readonly onViewFullImpact: () => void;
}

/**
 * Renders the sign action modal with transfer fee, salary, and duration inputs.
 * @param props - Component props
 * @param props.isOpen - Whether modal is visible
 * @param props.playerName - Player name for title
 * @param props.transferFee - Current transfer fee
 * @param props.annualSalary - Current annual salary
 * @param props.durationText - Duration in months as text
 * @param props.signingBonus - Current signing bonus value
 * @param props.agentFee - Current agent fee value
 * @param props.onTransferFeeChange - Transfer fee change handler
 * @param props.onAnnualSalaryChange - Annual salary change handler
 * @param props.onDurationChange - Duration change handler
 * @param props.onSigningBonusChange - Signing bonus change handler
 * @param props.onAgentFeeChange - Agent fee change handler
 * @param props.onConfirm - Confirm handler
 * @param props.onCancel - Cancel/close handler
 * @param props.onCancelAction - Remove action handler (edit mode)
 * @param props.isEditMode - Whether in edit mode
 * @param props.liveImpact - Live Impact verdict for the whole scenario
 * @param props.onViewFullImpact - Opens the full impact view
 */
const SignActionModalView = ({
  isOpen,
  playerName,
  transferFee,
  annualSalary,
  durationText,
  signingBonus,
  agentFee,
  onTransferFeeChange,
  onAnnualSalaryChange,
  onDurationChange,
  onSigningBonusChange,
  onAgentFeeChange,
  onConfirm,
  onCancel,
  onCancelAction,
  isEditMode = false,
  liveImpact,
  onViewFullImpact,
}: SignActionModalViewProps) => (
  <Modal isOpen={isOpen} onClose={onCancel} size="md" avoidKeyboard>
    <ModalBackdrop />
    <ModalContent className="p-6">
      <ModalHeader>
        <Heading variant="title-lg">
          Sign{" "}
          <Text variant="title" className="text-accent-primary">
            {playerName}
          </Text>
        </Heading>
      </ModalHeader>
      <ModalBody>
        <Stack className="gap-4">
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Transfer Fee
            </Text>
            <MoneyInput
              value={transferFee}
              onConfirm={onTransferFeeChange}
              onChange={onTransferFeeChange}
              onCancel={onCancel}
              variant="expanded"
            />
          </Stack>
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Annual Salary
            </Text>
            <MoneyInput
              value={annualSalary}
              onConfirm={onAnnualSalaryChange}
              onChange={onAnnualSalaryChange}
              onCancel={onCancel}
              variant="expanded"
            />
          </Stack>
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Contract Length (months)
            </Text>
            <TextInput
              testID="sign-duration-input"
              className="web:outline-none web:focus:border-accent-primary h-9 w-full rounded-sm border border-outline-default px-3 py-2 text-body text-content-primary"
              value={durationText}
              onChangeText={onDurationChange}
              placeholder="12"
              placeholderTextColor="#a3a3a3"
              keyboardType="number-pad"
            />
          </Stack>
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Signing Bonus
            </Text>
            <MoneyInput
              value={signingBonus}
              onConfirm={onSigningBonusChange}
              onChange={onSigningBonusChange}
              onCancel={onCancel}
              variant="expanded"
            />
          </Stack>
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Agent Fee
            </Text>
            <MoneyInput
              value={agentFee}
              onConfirm={onAgentFeeChange}
              onChange={onAgentFeeChange}
              onCancel={onCancel}
              variant="expanded"
            />
          </Stack>
          <LiveImpactPanel
            testID="sign-live-impact"
            model={liveImpact}
            onViewFullImpact={onViewFullImpact}
          />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Stack
          direction="horizontal"
          className="flex-1 flex-wrap items-center justify-between gap-2"
        >
          {isEditMode && onCancelAction != null ? (
            <Button
              variant="link"
              onPress={onCancelAction}
              size="md"
              label="Cancel Sign"
              labelTone="danger"
            />
          ) : (
            <Stack direction="horizontal" />
          )}
          <Stack direction="horizontal" className="gap-4">
            <Button
              variant="outline"
              onPress={onCancel}
              size="md"
              className="border-outline-strong"
              label={isEditMode ? "Close" : "Cancel"}
              labelTone="primary"
            />
            <Button
              testID="sign-confirm-button"
              onPress={onConfirm}
              size="md"
              className="bg-accent-primary"
              // SE-5100: transfer fee is intentionally NOT part of this
              // gate — free transfers and loan signings have no fee, so a
              // blank (null) or 0 fee must still allow Confirm. The store
              // serializes a blank fee as `signingFee: null`, which the
              // nullable FinancialDetailsInput.signingFee accepts.
              isDisabled={
                annualSalary == null ||
                annualSalary <= 0 ||
                !durationText.trim() ||
                Number.parseInt(durationText, 10) <= 0
              }
              label={isEditMode ? "Update" : "Confirm"}
              labelTone="on-accent"
            />
          </Stack>
        </Stack>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

SignActionModalView.displayName = "SignActionModalView";

export default memo(SignActionModalView);
